//
//  icons.ts
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module defines icons application behaviour.
//

import {
    faAddressCard,
    faArrowsRotate,
    faAsterisk,
    faBarcode,
    faBookOpen,
    faBriefcase,
    faBuilding,
    faChartLine,
    faChartSimple,
    faCheck,
    faChevronDown,
    faChevronLeft,
    faChevronRight,
    faChevronUp,
    faCircleQuestion,
    faClipboard,
    faClipboardClock,
    faClipboardList,
    faClock,
    faCloudDownload,
    faCodeBranch,
    faCogs,
    faColumns3,
    faComment,
    faCopy,
    faDatabase,
    faDna,
    faEllipsisVertical,
    faEnvelope,
    faEnvelopeOpenText,
    faExclamationTriangle,
    faExternalLink,
    faEye,
    faFile,
    faFileChartColumn,
    faFileMagnifyingGlass,
    faFlagCheckered,
    faFlaskVial,
    faFolder,
    faGlobe,
    faHandshake,
    faHeading,
    faHome,
    faHospitalAlt,
    faIdBadge,
    faIdCardClip,
    faInfoCircle,
    faInfoSquare,
    faInputText,
    faKey,
    faLink,
    faList,
    faListCheck,
    faLocationDot,
    faLock,
    faLockOpen,
    faMagnifyingGlass,
    faMap,
    faMegaphone,
    faMicroscope,
    faMonitorWaveform,
    faMoon,
    faSitemap,
    faPaperPlane,
    faPencil,
    faPerson,
    faPlug,
    faPlus,
    faPresentationScreen,
    faPuzzlePiece,
    faQuestion,
    faQuestionSquare,
    faRoute,
    faRows3,
    faShieldCheck,
    faSignIn,
    faSignOut,
    faSlidersSimple,
    faStar,
    faStickyNote,
    faSun,
    faTableCells,
    faTableList,
    faTableTree,
    faTag,
    faThumbtack,
    faTimes,
    faTimesCircle,
    faTimesHexagon,
    faTimesOctagon,
    faTimesSquare,
    faTrashCan,
    faTrashCanUndo,
    faUser,
    faUserDoctor,
    faVenusMars,
    faWavesSine
} from "@fortawesome/duotone-regular-svg-icons";
import type { AppIconLayer, AppIconDefinition } from "src/components/common/Icon";
import { place } from "src/components/common/iconComposition";

const em = 16;

// Each noun is 70% of the one before it, and each is centred on its parent's bottom-right
// corner so it sits half outside rather than nested inside it — nesting them in the corner is
// what made the deeper badges pile up with no daylight between them. The stack is then
// normalised to fill the em, which is where the leading factor comes from: solving
// "first layer's top-left on the em edge, last layer's bottom-right on it" for n layers gives
// S = 2 * em / (1 + (1 - r^(n-1))/(1 - r) + r^(n-1)).
const ratio = 0.7;

/**
 * Places each noun on the normalized descending ladder, retaining its contributed layers.
 *
 * Postconditions: Later nouns receive knockout flags; position advances in input order.
 * Empty input returns no layers. Geometry uses the fixed ratio without altering definitions.
 */
const ladder = (icons: readonly AppIconDefinition[]): AppIconLayer[][] => {
    const n = icons.length;
    const tail = n === 1 ? 0 : (1 - ratio ** (n - 1)) / (1 - ratio);
    const size = (2 * em) / (1 + tail + ratio ** (n - 1));
    let centre = -em / 2 + size / 2;

    return icons.map((definition, index) => {
        const layerSize = size * ratio ** index;
        const placed = place(definition, layerSize / em, centre, centre).map(layer =>
            index === 0 ? layer : { ...layer, knockout: true }
        );
        centre += layerSize / 2;

        return placed;
    });
};

// Below the container-query threshold the chain sheds its leading nouns — the broad context —
// and the survivors are re-laid out to fill the em, so the most specific noun stays legible
// rather than shrinking to a few pixels. Two-noun icons already read fine and keep both.
const compactDepth = 2;

/**
 * Builds a descending badge composition with compact placement for the final two nouns.
 *
 * Postconditions: Up to two nouns return the full flattened layout. Longer chains mark earlier
 * nouns compact false and pair surviving layers with compact transforms by position.
 * Input definitions are not mutated; empty input returns an empty layer list.
 */
export const badge = (...icons: readonly AppIconDefinition[]): AppIconDefinition => {
    const full = ladder(icons);

    if (icons.length <= compactDepth) {
        return full.flat();
    }

    // Pair the surviving nouns with their compact placement noun-by-noun, so a noun that
    // contributes several layers still lines up with its shortened self.
    const kept = ladder(icons.slice(-compactDepth));
    const firstKept = icons.length - compactDepth;

    return full.flatMap((layers, noun) =>
        layers.map((layer, within) =>
            noun < firstKept
                ? { ...layer, compact: false as const }
                : { ...layer, compact: kept[noun - firstKept][within].transform }
        )
    );
};

// Compound symbols descend in the order the model name reads: the outermost noun is the base
// glyph and each following one becomes a smaller badge, so trialInstitutionItem is a trial
// carrying an institution carrying an item.
const trialInstitutionItem = badge(faHospitalAlt, faClipboardList);
const trialGroupItem = badge(faSitemap, faClipboardList);
const trialForm = badge(faDna, faTableTree);
const institutionForm = badge(faHospitalAlt, faTableTree);

const models = {
    trial: faDna,
    institution: faHospitalAlt,
    trialInstitution: badge(faDna, faHospitalAlt),
    trialInstitutionSupportingSite: badge(faHospitalAlt, faHandshake),
    department: faBuilding,
    group: faSitemap,
    trialGroup: badge(faDna, faSitemap),
    personnel: faUser,
    participant: faPerson,
    personnelRole: badge(faUser, faIdBadge),
    departmentContact: badge(faBuilding, faAddressCard),
    role: faIdCardClip,
    permission: faKey,
    openClinicaUserRole: badge(faUser, faIdCardClip),
    note: faStickyNote,
    notice: faMegaphone,
    helpTopic: faCircleQuestion,
    trialNote: faStickyNote,
    institutionNote: faStickyNote,
    trialInstitutionNote: faStickyNote,
    groupNote: faStickyNote,
    personnelNote: faStickyNote,
    sourceDocument: faFile,
    emailLog: faEnvelope,
    emailContext: badge(faEnvelope, faTag),
    emailContextCategory: badge(faEnvelope, faFolder),
    emailRecipientKind: badge(faEnvelope, faList),
    emailRecipientConfiguration: badge(faEnvelope, faSlidersSimple),
    emailRecipientConfigurationItem: badge(faEnvelope, faAddressCard),
    personnelEmailPreference: badge(faUser, faSlidersSimple),
    country: faGlobe,
    state: faMap,
    salutation: faAddressCard,
    gender: faVenusMars,
    interest: faStar,
    membershipType: faIdBadge,
    professionalDiscipline: faUserDoctor,
    noteType: badge(faStickyNote, faTag),
    itemComponentType: badge(faPuzzlePiece, faTag),
    trialCategory: badge(faDna, faFolder),
    trialType: badge(faDna, faTag),
    trialPhase: faWavesSine,
    tumourGroup: faMicroscope,
    allocatedWP: faBriefcase,
    involvement: faHandshake,
    trialAnalysisType: badge(faDna, faChartSimple),
    trialPresentationType: badge(faDna, faPresentationScreen),
    trialPublicationType: badge(faDna, faBookOpen),
    trialTreatment: faFlaskVial,
    trialMilestones: faFlagCheckered,
    trialApi: faPlug,
    trialTracker: faMonitorWaveform,
    trialTrackerComment: faComment,
    qaProgramChecklist: faListCheck,
    qaProgramChecklistCategory: badge(faListCheck, faFolder),
    qaProgramChecklistItem: badge(faListCheck, faCheck),
    accrual: faChartLine,
    siteLocation: faLocationDot,
    facilityAllianceMember: badge(faHospitalAlt, faLink),
    institutionType: badge(faHospitalAlt, faTag),
    institutionCoding: faBarcode,
    institutionCapability: faShieldCheck,
    institutionCapabilityCategory: badge(faShieldCheck, faFolder),
    institutionTag: faTag,
    institutionTagCategory: badge(faTag, faFolder),
    patientTag: badge(faPerson, faTag),
    patientTagCategory: badge(faPerson, faFolder),
    trialCapability: faShieldCheck,
    trialInstitutionCapability: faShieldCheck,
    trialInstitutionSupportingSiteCapability: faShieldCheck,
    trialTag: faTag,
    trialInstitutionTag: faTag,
    trialInstitutionSupportingSiteTag: faTag,
    item: faClipboardList,
    trialItem: badge(faDna, faClipboardList),
    trialInstitutionItem: trialInstitutionItem,
    trialGroupItem: trialGroupItem,
    trialItemTemplate: badge(faDna, faClipboard),
    trialInstitutionItemTemplate: badge(faHospitalAlt, faClipboard),
    patientItemTemplate: badge(faPerson, faClipboard),
    patientItem: badge(faPerson, faClipboardList),
    trialGroupItemTemplate: badge(faSitemap, faClipboard),
    category: faFolder,
    itemComponent: faPuzzlePiece,
    itemComponentPrimitive: faSlidersSimple,
    itemComponentPrimitiveOption: faList,
    itemComponentRule: faCodeBranch,
    formTemplate: badge(faTableList, faTableTree),
    formTemplateCategory: badge(faTableTree, faFolder),
    trialForm: trialForm,
    institutionForm: institutionForm,
    trialItemVersion: faClock,
    trialInstitutionItemVersion: faClock,
    trialGroupItemVersion: faClock,
    trialFormVersion: faClock,
    institutionFormVersion: faClock,
    collection: faTableCells,
    collectionSection: faRows3,
    collectionColumn: faColumns3,
    collectionItemComponent: badge(faTableCells, faPuzzlePiece),
    collectionItemComponentOverride: badge(faPuzzlePiece, faSlidersSimple),
    collectionInstance: faDatabase,
    collectionInstanceSection: badge(faDatabase, faRows3),
    taskType: faListCheck,
    trackerType: badge(faMonitorWaveform, faTag),
    trackerMilestone: faFlagCheckered,
    reportingTag: badge(faFileChartColumn, faTag),
    reportingTagCategory: badge(faFileChartColumn, faFolder),
    formTemplateFileType: badge(faFile, faTag),
    standardEmailTemplate: faEnvelopeOpenText,
    trialFormReport: faFileChartColumn,
    administrationCaseReport: faFileChartColumn,
    trialFormReportSection: faHeading,
    trialFormReportDocument: faFile
} satisfies Record<string, AppIconDefinition>;

export type ModelIconName = keyof typeof models;

export interface IErrorIconSettings {
    description: AppIconDefinition;
    information: AppIconDefinition;
    warning: AppIconDefinition;
    low: AppIconDefinition;
    medium: AppIconDefinition;
    high: AppIconDefinition;
    critical: AppIconDefinition;
}

export interface IIconSettings {
    models: typeof models;
    activate: AppIconDefinition;
    add: AppIconDefinition;
    arrowLeft: AppIconDefinition;
    arrowRight: AppIconDefinition;
    arrowUp: AppIconDefinition;
    arrowDown: AppIconDefinition;
    close: AppIconDefinition;
    comments: AppIconDefinition;
    columns: AppIconDefinition;
    copy: AppIconDefinition;
    dashboard: AppIconDefinition;
    deactivate: AppIconDefinition;
    delete: AppIconDefinition;
    department: AppIconDefinition;
    download: AppIconDefinition;
    edit: AppIconDefinition;
    email: AppIconDefinition;
    error: IErrorIconSettings;
    externalLink: AppIconDefinition;
    group: AppIconDefinition;
    help: AppIconDefinition;
    information: AppIconDefinition;
    institution: AppIconDefinition;
    locked: AppIconDefinition;
    unlocked: AppIconDefinition;
    lockedParticipant: AppIconDefinition;
    login: AppIconDefinition;
    logout: AppIconDefinition;
    lookup: AppIconDefinition;
    menu: AppIconDefinition;
    mergeField: AppIconDefinition;
    noAccess: AppIconDefinition;
    note: AppIconDefinition;
    participant: AppIconDefinition;
    pin: AppIconDefinition;
    preferences: AppIconDefinition;
    preview: AppIconDefinition;
    previousVersion: AppIconDefinition;
    question: AppIconDefinition;
    reportABug: AppIconDefinition;
    required: AppIconDefinition;
    search: AppIconDefinition;
    send: AppIconDefinition;
    settings: AppIconDefinition;
    success: AppIconDefinition;
    synchronize: AppIconDefinition;
    themeDark: AppIconDefinition;
    themeLight: AppIconDefinition;
    tour: AppIconDefinition;
    treatment: AppIconDefinition;
    trial: AppIconDefinition;
    trialInstitution: AppIconDefinition;
    trialItem: AppIconDefinition;
    trialTracker: AppIconDefinition;
    unknown: AppIconDefinition;
    user: AppIconDefinition;
    view: AppIconDefinition;
}

export const icons: IIconSettings = {
    models,
    activate: faTrashCanUndo,
    add: faPlus,
    arrowLeft: faChevronLeft,
    arrowRight: faChevronRight,
    arrowDown: faChevronDown,
    arrowUp: faChevronUp,
    close: faTimes,
    comments: faComment,
    columns: faColumns3,
    copy: faCopy,
    dashboard: faHome,
    deactivate: faTrashCan,
    delete: faTimes,
    department: models.department,
    download: faCloudDownload,
    edit: faPencil,
    email: faEnvelope,
    error: {
        description: faInfoSquare,
        information: faInfoCircle,
        warning: faExclamationTriangle,
        low: faTimesCircle,
        medium: faTimesSquare,
        high: faTimesHexagon,
        critical: faTimesOctagon
    },
    externalLink: faExternalLink,
    group: models.group,
    help: faCircleQuestion,
    information: faInfoCircle,
    institution: models.institution,
    locked: faLock,
    unlocked: faLockOpen,
    lockedParticipant: badge(faPerson, faLock),
    login: faSignIn,
    logout: faSignOut,
    lookup: faList,
    menu: faEllipsisVertical,
    mergeField: faInputText,
    noAccess: faIdBadge,
    note: models.note,
    participant: models.participant,
    pin: faThumbtack,
    preferences: faSlidersSimple,
    preview: faFileMagnifyingGlass,
    previousVersion: faClipboardClock,
    question: faQuestionSquare,
    reportABug: faMegaphone,
    required: faAsterisk,
    search: faMagnifyingGlass,
    send: faPaperPlane,
    settings: faCogs,
    success: faCheck,
    synchronize: faArrowsRotate,
    themeDark: faMoon,
    themeLight: faSun,
    tour: faRoute,
    treatment: models.trialTreatment,
    trial: models.trial,
    trialInstitution: models.trialInstitution,
    trialItem: models.trialItem,
    trialTracker: models.trialTracker,
    unknown: faQuestion,
    user: models.personnel,
    view: faEye
};
